import React from "react";
import { useTranslation } from "@/lib/i18n";

/**
 * HeroCapabilityStrip — quiet Stockaily Hero credibility row.
 *
 * Composition:
 *   • One standalone 36 px wireframe globe (see RotatingGlobe below).
 *   • Two concise text statements separated by a subtle centred dot.
 *   • No pill background, no card frame — subordinate to the H1.
 */
export default function HeroCapabilityStrip({ className = "" }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="hero-capability-strip"
      className={`flex flex-col sm:flex-row items-center justify-center gap-y-2 gap-x-4 text-center ${className}`}
      aria-label={t("landing.capability_group_aria")}
    >
      <RotatingGlobe />
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 max-w-xl">
        <span
          data-testid="hero-capability-primary"
          className="text-[13px] sm:text-[13.5px] leading-snug font-medium text-foreground/90 tracking-tight"
        >
          {t("landing.capability_primary")}
        </span>
        <span
          aria-hidden="true"
          className="hidden sm:inline text-muted-foreground/40 select-none"
        >
          &middot;
        </span>
        <span
          data-testid="hero-capability-secondary"
          className="text-[12px] sm:text-[13px] leading-snug text-muted-foreground/85"
        >
          {t("landing.capability_secondary")}
        </span>
      </div>
    </div>
  );
}

/**
 * RotatingGlobe — schematic 2D wireframe globe with the illusion of
 * rotation around the VERTICAL axis (i.e. a real globe turning
 * horizontally). Previous implementation spun the whole SVG around
 * its centre which caused the equator and outer circle to tilt like
 * a wheel — that was the incorrect motion.
 *
 * Fix technique (lightweight, no library, no 3D):
 *   • Outer circle, equator, and two latitude bands are RENDERED ONCE
 *     and never transformed. They stay perfectly upright at all times.
 *   • The perception of horizontal rotation comes from TWO meridian
 *     ellipses that continuously vary their horizontal radius via
 *     CSS `transform: rotateY(...)`. Without `preserve-3d`, browsers
 *     project rotateY as a horizontal squeeze (scaleX = cos(angle)) —
 *     which is exactly the way a meridian looks as it turns around
 *     the vertical axis.
 *   • The two meridians run the SAME 20 s cycle but are offset by
 *     180° via a negative `animation-delay`. When one meridian is
 *     edge-on (invisible), the other is fully facing the viewer —
 *     giving a continuous, seamless left-to-right rotation without
 *     any abrupt reset at the loop boundary.
 *   • `transform-box: fill-box; transform-origin: center;` keeps the
 *     rotation axis exactly on the globe's vertical centreline.
 *   • `@media (prefers-reduced-motion: reduce)` disables both
 *     animations — under reduced-motion the globe renders as a
 *     completely static wireframe with two crossed meridians visible.
 */
function RotatingGlobe() {
  return (
    <>
      <style>{`
        @keyframes hcs-globe-rotY {
          0%   { transform: rotateY(0deg); }
          100% { transform: rotateY(360deg); }
        }
        .hcs-meridian {
          transform-box: fill-box;
          transform-origin: center;
          animation: hcs-globe-rotY 20s linear infinite;
          will-change: transform;
        }
        .hcs-meridian--a { animation-delay: 0s; }
        .hcs-meridian--b { animation-delay: -10s; }
        @media (prefers-reduced-motion: reduce) {
          .hcs-meridian { animation: none !important; }
        }
      `}</style>
      <svg
        viewBox="0 0 40 40"
        role="presentation"
        aria-hidden="true"
        focusable="false"
        data-testid="hero-capability-globe"
        className="w-9 h-9 shrink-0 text-[#3182CE]"
        style={{
          filter: "drop-shadow(0 0 6px rgba(49,130,206,0.35))",
        }}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* Static frame — never transformed, never tilts. */}
        <circle cx="20" cy="20" r="15.5" strokeWidth="1.4" />
        <path d="M4.5 20h31" strokeWidth="1.2" />
        <path
          d="M7 12.5c4 1.5 22 1.5 26 0M7 27.5c4-1.5 22-1.5 26 0"
          strokeWidth="0.9"
          opacity="0.7"
        />

        {/* Two meridian ellipses, 180° out of phase. The horizontal
            projection of rotateY() creates the impression that a
            single meridian is smoothly travelling around the globe. */}
        <ellipse
          className="hcs-meridian hcs-meridian--a"
          cx="20"
          cy="20"
          rx="7"
          ry="15.5"
          strokeWidth="1.1"
          data-testid="hero-capability-globe-meridian-a"
        />
        <ellipse
          className="hcs-meridian hcs-meridian--b"
          cx="20"
          cy="20"
          rx="7"
          ry="15.5"
          strokeWidth="1.1"
          data-testid="hero-capability-globe-meridian-b"
        />
      </svg>
    </>
  );
}
